// src/contracts/schema/http.ts
var createContract = (method, router, route, request, response) => {
  return {
    method,
    router,
    route,
    path: `${router}${route}`,
    request,
    response
  };
};
var createFunctionalContract = (contract) => {
  const fetch = async (origin, init, implFetch = globalThis.fetch) => {
    let url = `${origin}${contract.path}`;
    const modifiedInit = { ...init };
    if (init && "pathParams" in init && init.pathParams) {
      for (const [key, value] of Object.entries(init.pathParams)) {
        url = url.replace(`:${key}`, String(value));
      }
    }
    if (init && "queryParams" in init && init.queryParams) {
      url += "?";
      const queryParams = new URLSearchParams();
      for (const [key, value] of Object.entries(init.queryParams)) {
        queryParams.set(key, String(value));
      }
      url += queryParams.toString();
    }
    if (init && "body" in modifiedInit && modifiedInit.body && typeof modifiedInit.body === "object") {
      modifiedInit.body = JSON.stringify(modifiedInit.body);
    }
    return await implFetch(url, {
      ...modifiedInit
    });
  };
  return {
    ...contract,
    fetch
  };
};

// src/contracts/data-dump-http.ts
import * as z from "zod/v4";
var DataDumpSourceSchema = z.union([
  z.literal("machine-learning-face-detection"),
  z.literal("machine-learning-object-detection")
]);
var DataDumpMimeTypeSchema = z.union([
  z.literal("application/json"),
  z.literal("application/octet-stream"),
  z.literal("application/xml")
]);
var DataDumpGrantContract = createFunctionalContract(createContract(
  "POST",
  "/data-dump",
  "/:proctoringSessionUuid/grant",
  {
    pathParams: z.object({
      proctoringSessionUuid: z.string()
    }),
    body: z.object({
      source: DataDumpSourceSchema,
      mimeType: DataDumpMimeTypeSchema
    })
  },
  {
    body: z.object({
      grantId: z.string(),
      uploadUrl: z.string()
    })
  }
));
var DataDumpCallbackContract = createFunctionalContract(createContract(
  "POST",
  "/data-dump",
  "/:proctoringSessionUuid/callback",
  {
    pathParams: z.object({
      proctoringSessionUuid: z.string()
    }),
    body: z.object({
      grantId: z.string()
    })
  },
  {}
));
var DataDumpLinksContract = createFunctionalContract(createContract(
  "GET",
  "/data-dump",
  "/:proctoringSessionUuid/links",
  {
    pathParams: z.object({
      proctoringSessionUuid: z.string()
    })
  },
  {
    body: z.object({
      links: z.array(z.object({
        source: DataDumpSourceSchema,
        mimeType: DataDumpMimeTypeSchema,
        downloadUrl: z.string()
      }))
    })
  }
));

// src/contracts/logging-http.ts
import * as z2 from "zod/v4";
var MachineLearningEventSchema = z2.object({
  type: z2.literal("machine-learning"),
  time: z2.date()
});
var FaceDetectionStateSchema = z2.union([
  z2.literal("detected-no-face"),
  z2.literal("detected-target-face"),
  z2.literal("detected-unknown-face"),
  z2.literal("detected-many-faces")
]);
var FaceDetectionEventSchema = MachineLearningEventSchema.extend({
  name: z2.literal("face-detection"),
  details: z2.object({
    previousState: FaceDetectionStateSchema,
    currentState: FaceDetectionStateSchema
  })
});
var TargetObjectsSchema = z2.union([
  z2.literal("cell-phone"),
  z2.literal("laptop"),
  z2.literal("book")
]);
var ObjectDetectionStateSchema = z2.union([
  z2.literal("detected-no-object"),
  z2.templateLiteral(["detected-", TargetObjectsSchema], "-object"),
  z2.literal("detected-many-objects")
]);
var ObjectDetectionEventSchema = MachineLearningEventSchema.extend({
  name: z2.literal("object-detection"),
  details: z2.object({
    previousState: ObjectDetectionStateSchema,
    currentState: ObjectDetectionStateSchema
  })
});
var LogMachineLearningQuizEventContract = createFunctionalContract(createContract(
  "POST",
  "/quiz/event",
  "/:proctoringSessionUuid",
  {
    pathParams: z2.object({
      proctoringSessionUuid: z2.string()
    }),
    body: z2.union([
      FaceDetectionEventSchema,
      ObjectDetectionEventSchema
    ])
  },
  {}
));

// src/contracts/restricted-device-event.ts
import * as z4 from "zod/v4";

// src/contracts/schema/event.ts
import * as z3 from "zod/v4";
var createEventContract = (namespace, handshake, events) => {
  return {
    namespace,
    queryParams: handshake.queryParams,
    events
  };
};
var createDiscriminableEventPayload = (key, schema) => {
  return z3.object({
    key: z3.literal(key),
    ...schema.shape
  });
};
var createDiscriminableEvent = (...args) => {
  return z3.discriminatedUnion("key", args);
};

// src/contracts/restricted-device-event.ts
var RestrictedDeviceStateSchema = z4.object({
  isLocked: z4.boolean()
});
var RestrictedDeviceEventContract = createEventContract(
  "/restricted-device",
  {
    queryParams: z4.object({
      secret: z4.string(),
      peerId: z4.string()
    })
  },
  {
    message: createDiscriminableEvent(
      createDiscriminableEventPayload("pollRequest", z4.object({})),
      createDiscriminableEventPayload("pollResponse", z4.object({
        devices: z4.array(z4.object({
          peerId: z4.string(),
          connected: z4.boolean(),
          data: z4.any()
        }))
      })),
      createDiscriminableEventPayload("stateUpdate", RestrictedDeviceStateSchema)
    )
  }
);

// src/contracts/restricted-device-http.ts
import * as z6 from "zod/v4";

// src/contracts/restricted-device-common.ts
import * as z5 from "zod/v4";
var RestrictedDeviceRoleSchema = z5.union(
  [
    z5.literal("source"),
    z5.literal("sink")
  ]
);
var RestrictedDevicePlatformSchema = z5.union(
  [
    z5.literal("extension"),
    z5.literal("mobile"),
    z5.literal("lockdown")
  ]
);

// src/contracts/restricted-device-http.ts
var RestrictedDeviceRegistrationContract = createFunctionalContract(
  createContract(
    "POST",
    "/restricted-device",
    "/register",
    {
      body: z6.object({
        role: RestrictedDeviceRoleSchema,
        platform: RestrictedDevicePlatformSchema,
        proctoringSessionUuid: z6.string()
      })
    },
    {
      body: z6.object({
        peerId: z6.string()
      })
    }
  )
);
var RestrictedDeviceGrantContract = createFunctionalContract(
  createContract(
    "POST",
    "/restricted-device",
    "/:restrictedDevicePeerId/grant",
    {
      pathParams: z6.object({
        restrictedDevicePeerId: z6.string()
      })
    },
    {
      body: z6.object({
        secret: z6.string()
      })
    }
  )
);
export {
  DataDumpCallbackContract,
  DataDumpGrantContract,
  DataDumpLinksContract,
  DataDumpMimeTypeSchema,
  DataDumpSourceSchema,
  FaceDetectionEventSchema,
  FaceDetectionStateSchema,
  LogMachineLearningQuizEventContract,
  MachineLearningEventSchema,
  ObjectDetectionEventSchema,
  ObjectDetectionStateSchema,
  RestrictedDeviceEventContract,
  RestrictedDeviceGrantContract,
  RestrictedDeviceRegistrationContract,
  RestrictedDeviceStateSchema,
  TargetObjectsSchema
};
//# sourceMappingURL=index.js.map